import {
  RealEstateCard,
  type RealEstateItem,
} from "@/components/RealEstateCard";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/contexts/AuthContext";
import { useRegion } from "@/contexts/RegionContext";
import { useZone } from "@/contexts/ZoneContext";
import { australianCities, regions } from "@shared/schema";
import { useQuery } from "@tanstack/react-query";
import { Calendar, Home, MapPin } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "wouter";

export default function RealEstate() {
  const { user } = useAuth();
  const { selectedRegion, selectedSuburb } = useRegion();
  const { selectedZoneId } = useZone();
  const [selectedState, setSelectedState] = useState<string>(
    selectedRegion || "ALL"
  );
  const [selectedCity, setSelectedCity] = useState<string>("ALL");
  const [searchTerm, setSearchTerm] = useState<string>("");
  // Phase 3.2: 부동산 전용 필터
  const [minPrice, setMinPrice] = useState<string>("");
  const [maxPrice, setMaxPrice] = useState<string>("");
  const [propertyType, setPropertyType] = useState<string>("all"); // all, rent, sale, share
  const [moveInDate, setMoveInDate] = useState<string>(""); // 입주일 필터
  const [bedrooms, setBedrooms] = useState<string>("all"); // all, 1, 2, 3, 4+
  const [parking, setParking] = useState<boolean>(false);
  const [furnished, setFurnished] = useState<boolean>(false);

  // Phase 3.2: 부동산 목록 조회 (category: 'housing' 필터)
  const { data: listings, isLoading } = useQuery<RealEstateItem[]>({
    queryKey: ["/api/unified-market", selectedZoneId, selectedState, selectedCity, "housing"],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append("category", "housing");
      params.append("type", "ad");
      params.append("zone", selectedZoneId);
      if (selectedState !== "ALL") params.append("state", selectedState);
      if (selectedCity !== "ALL") params.append("city", selectedCity);

      const res = await fetch(`/api/unified-market?${params.toString()}`);
      if (!res.ok) throw new Error("Failed to fetch listings");
      const data = await res.json();
      // 응답이 { ok: true, data: { items: [...] } } 형식일 수 있음
      if (data?.ok && data?.data?.items) {
        return data.data.items;
      }
      if (Array.isArray(data)) {
        return data;
      }
      return [];
    },
  });

  // Phase 3.2: 고급 필터링 로직
  const filteredListings = useMemo(() => {
    if (!listings) return [];

    let result = listings.filter((listing) => {
      // 검색어 필터
      if (
        searchTerm &&
        !listing.title.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !listing.description?.toLowerCase().includes(searchTerm.toLowerCase())
      ) {
        return false;
      }

      // 가격범위 필터
      if (minPrice || maxPrice) {
        const price = listing.priceCents;
        if (
          price === null ||
          price === undefined ||
          price === -1 ||
          price === 0
        ) {
          // 가격협의는 필터에서 제외하지 않음
        } else {
          const priceInDollars = price / 100;
          if (minPrice && priceInDollars < parseFloat(minPrice)) return false;
          if (maxPrice && priceInDollars > parseFloat(maxPrice)) return false;
        }
      }

      // 부동산 타입 필터
      if (propertyType !== "all" && listing.propertyType !== propertyType) {
        return false;
      }

      // 방개수 필터
      if (bedrooms !== "all" && listing.bedrooms !== undefined) {
        const bedCount = parseInt(bedrooms);
        if (bedrooms === "4+") {
          if (listing.bedrooms < 4) return false;
        } else {
          if (listing.bedrooms !== bedCount) return false;
        }
      }

      // 주차 필터
      if (parking && !listing.parking) {
        return false;
      }

      // 가구 필터
      if (furnished && !listing.furnished) {
        return false;
      }

      // 입주일 필터 (입주일이 설정된 경우, 해당 날짜 이후만)
      if (moveInDate) {
        const filterDate = new Date(moveInDate);
        if (listing.moveInDate) {
          const listingDate = new Date(listing.moveInDate);
          if (listingDate < filterDate) return false;
        }
      }

      return true;
    });

    // 최신순 정렬
    return result.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [
    listings,
    searchTerm,
    minPrice,
    maxPrice,
    propertyType,
    bedrooms,
    parking,
    furnished,
    moveInDate,
  ]);

  const stateRegion = regions.find((r) => r.id === selectedState);
  const availableCities =
    selectedState !== "ALL" && selectedState in australianCities
      ? australianCities[selectedState as keyof typeof australianCities]
      : [];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <div className="bg-primary/5 border-b">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl font-bold mb-3" data-testid="text-title">
              부동산
            </h1>
            <p className="text-lg text-muted-foreground mb-6">
              렌트, 매매, 쉐어 - 호주 한인 커뮤니티 부동산 정보
            </p>
            <div className="flex gap-4 justify-center items-center flex-wrap">
              <Badge variant="secondary" className="gap-1">
                <Home className="w-3 h-3" />
                렌트/매매
              </Badge>
              <Badge variant="secondary" className="gap-1">
                <MapPin className="w-3 h-3" />
                지역기반
              </Badge>
              <Badge variant="secondary" className="gap-1">
                <Calendar className="w-3 h-3" />
                입주일 검색
              </Badge>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar - Filters */}
          <aside className="lg:col-span-1">
            <Card>
              <CardContent className="p-4 space-y-4">
                {/* Search */}
                <div>
                  <label className="text-sm font-medium mb-2 block">검색</label>
                  <Input
                    placeholder="제목 또는 설명 검색..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    data-testid="input-search"
                  />
                </div>

                {/* State Filter */}
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    주/지역
                  </label>
                  <Select
                    value={selectedState}
                    onValueChange={(value) => {
                      setSelectedState(value);
                      setSelectedCity("ALL");
                    }}
                  >
                    <SelectTrigger data-testid="select-state">
                      <SelectValue placeholder="주 선택" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">전체 지역</SelectItem>
                      {regions
                        .filter((r) => r.id !== "ALL")
                        .map((region) => (
                          <SelectItem key={region.id} value={region.id}>
                            {region.label}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* City Filter */}
                {availableCities.length > 0 && (
                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      도시
                    </label>
                    <Select
                      value={selectedCity}
                      onValueChange={setSelectedCity}
                    >
                      <SelectTrigger data-testid="select-city">
                        <SelectValue placeholder="도시 선택" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ALL">전체 도시</SelectItem>
                        {availableCities.map((city) => (
                          <SelectItem key={city.id} value={city.id}>
                            {city.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Phase 3.2: 가격범위 필터 */}
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    가격범위 (주당)
                  </label>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      placeholder="최소"
                      value={minPrice}
                      onChange={(e) => setMinPrice(e.target.value)}
                      className="flex-1"
                    />
                    <Input
                      type="number"
                      placeholder="최대"
                      value={maxPrice}
                      onChange={(e) => setMaxPrice(e.target.value)}
                      className="flex-1"
                    />
                  </div>
                </div>

                {/* Phase 3.2: 부동산 타입 필터 */}
                <div>
                  <label className="text-sm font-medium mb-2 block">타입</label>
                  <Select value={propertyType} onValueChange={setPropertyType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">전체</SelectItem>
                      <SelectItem value="rent">렌트</SelectItem>
                      <SelectItem value="sale">매매</SelectItem>
                      <SelectItem value="share">쉐어</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Phase 3.2: 방개수 필터 */}
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    방개수
                  </label>
                  <Select value={bedrooms} onValueChange={setBedrooms}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">전체</SelectItem>
                      <SelectItem value="1">1bed</SelectItem>
                      <SelectItem value="2">2bed</SelectItem>
                      <SelectItem value="3">3bed</SelectItem>
                      <SelectItem value="4+">4bed+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Phase 3.2: 입주일 필터 */}
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    입주일
                  </label>
                  <Input
                    type="date"
                    value={moveInDate}
                    onChange={(e) => setMoveInDate(e.target.value)}
                  />
                </div>

                {/* Phase 3.2: 주차 필터 */}
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="parking"
                    checked={parking}
                    onCheckedChange={(checked) => setParking(checked === true)}
                  />
                  <label
                    htmlFor="parking"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    주차 가능
                  </label>
                </div>

                {/* Phase 3.2: 가구 필터 */}
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="furnished"
                    checked={furnished}
                    onCheckedChange={(checked) =>
                      setFurnished(checked === true)
                    }
                  />
                  <label
                    htmlFor="furnished"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    가구 포함
                  </label>
                </div>

                {/* Post Button */}
                {user && (
                  <Link href="/real-estate/register">
                    <Button className="w-full" data-testid="button-register">
                      부동산 등록
                    </Button>
                  </Link>
                )}
                {!user && (
                  <Button className="w-full" variant="outline" disabled>
                    로그인 후 등록 가능
                  </Button>
                )}
              </CardContent>
            </Card>
          </aside>

          {/* Main Content - Listings */}
          <div className="lg:col-span-3">
            {/* Results Count */}
            <div className="mb-4 flex justify-between items-center">
              <p
                className="text-sm text-muted-foreground"
                data-testid="text-count"
              >
                {filteredListings
                  ? `${filteredListings.length}개의 매물`
                  : "로딩 중..."}
              </p>
            </div>

            {/* Listings Grid */}
            {isLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {[...Array(6)].map((_, i) => (
                  <Card key={i}>
                    <Skeleton className="h-48 w-full" />
                    <CardContent className="p-4">
                      <Skeleton className="h-4 w-3/4 mb-2" />
                      <Skeleton className="h-4 w-1/2" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : filteredListings && filteredListings.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {filteredListings.map((listing) => (
                  <RealEstateCard
                    key={listing.id}
                    item={listing}
                    onInquire={() => {
                      // TODO: 채팅 시작 로직
                      window.location.href = `/chat-start/real-estate/${listing.id}`;
                    }}
                  />
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="py-16 text-center">
                  <Home className="w-16 h-16 mx-auto mb-4 text-muted-foreground/30" />
                  <p className="text-lg font-medium mb-2">
                    등록된 매물이 없습니다
                  </p>
                  <p className="text-sm text-muted-foreground mb-6">
                    첫 번째 부동산 매물을 등록해보세요!
                  </p>
                  {user && (
                    <Link href="/real-estate/register">
                      <Button>부동산 등록하기</Button>
                    </Link>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
